import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, Home, Users, HelpCircle, UserCircle, Mail, Boxes } from "lucide-react";
import { Button } from "../components/ui/button";
import { useEffect } from "react";

export default function SideNavbar({ isOpen, menu, onCloseSideNav }) {
    const location = useLocation();
    
    // Prevent body scroll when menu is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const menuIcons = {
        'What we do': Home,
        'Products': Boxes,
        'Who we serve': Users,
        'Why Velozent': HelpCircle,
        'Who we are': UserCircle,
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div 
                        className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={onCloseSideNav}
                    />
                    
                    {/* Side Menu */}
                    <motion.div 
                        className="lg:hidden fixed top-0 right-0 h-full w-full max-w-sm bg-white shadow-2xl z-50 flex flex-col"
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "tween", duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                    >
                        {/* Header */}
                        <div className="flex justify-between items-center px-6 py-5 border-b border-gray-200">
                            <NavLink to="/" onClick={onCloseSideNav}>
                                <img
                                    src="/assets/images/logo.svg"
                                    alt="Velozent logo"
                                    className="h-10 w-auto contrast-[1.03] [transform:translateZ(0)]"
                                    decoding="async"
                                />
                            </NavLink>
                            <motion.button
                                className="p-2 -mr-2 hover:bg-gray-100 rounded-full transition-colors active:bg-gray-200"
                                onClick={onCloseSideNav}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                aria-label="Close menu"
                            >
                                <X className="h-6 w-6 text-gray-700" />
                            </motion.button>
                        </div>

                        {/* Menu Items */}
                        <nav className="flex-1 overflow-y-auto px-4 py-6">
                            <ul className="space-y-1">
                                {menu.map((item, index) => {
                                    const Icon = menuIcons[item.name] || ChevronRight;
                                    const isActive = location.pathname === `/${item.path}`;
                                    return (
                                        <motion.li 
                                            key={item.id}
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ 
                                                duration: 0.3, 
                                                delay: index * 0.08,
                                                ease: "easeOut"
                                            }}
                                        >
                                            <NavLink
                                                onClick={onCloseSideNav}
                                                to={item.path}
                                                className={({ isActive: navIsActive }) =>
                                                    `flex items-center justify-between px-4 py-4 rounded-lg transition-all duration-200 ${
                                                        navIsActive 
                                                            ? "bg-burnt-sienna-50 text-burnt-sienna-600 font-semibold" 
                                                            : "text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                                                    }`
                                                }
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Icon className={`h-5 w-5 transition-colors ${
                                                        isActive ? 'text-burnt-sienna-500' : 'text-gray-500'
                                                    }`} />
                                                    <span className="text-base font-medium">{item.name}</span>
                                                </div>
                                                <ChevronRight className={`h-5 w-5 transition-colors ${
                                                    isActive ? 'text-burnt-sienna-400' : 'text-gray-400'
                                                }`} />
                                            </NavLink>
                                        </motion.li>
                                    );
                                })}
                            </ul>
                        </nav>

                        {/* Footer with Contact Button */}
                        <div className="border-t border-gray-200 px-4 py-6 bg-gray-50">
                            <Button 
                                asChild 
                                size="lg" 
                                className="w-full text-base font-semibold"
                                onClick={onCloseSideNav}
                            >
                                <Link to="/contact" className="flex items-center justify-center gap-2">
                                    <Mail className="h-5 w-5" />
                                    Contact Us
                                </Link>
                            </Button>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    )
}
